import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ContentPost } from 'api/new-analytics/types';
import { buildReportPostTableGroups } from 'app/modules/analytics/modules/reports/utils/buildReportPostTableGroups';
import { useReportPreviewRenderReadyBlock } from 'app/modules/analytics/modules/reports/context/ReportPreviewRenderReadyContext';
import ReportContentPostTableCard from './ReportContentPostTableCard';

interface Props {
  posts: ContentPost[];
  isLoading?: boolean;
}

const ReportPreviewTopPostsSection = ({ posts, isLoading = false }: Props) => {
  const { t } = useTranslation();
  const { markReady } = useReportPreviewRenderReadyBlock('posts', {
    enabled: !isLoading && posts.length > 0,
  });

  const groups = useMemo(() => buildReportPostTableGroups(posts), [posts]);

  useEffect(() => {
    if (!isLoading && posts.length > 0) {
      markReady();
    }
  }, [isLoading, markReady, posts.length]);

  if (isLoading) {
    return null;
  }

  if (posts.length === 0) {
    return (
      <section className="tw-space-y-3">
        <h3 className="tw-text-sm tw-font-semibold tw-text-grayscale-180">
          {t('analyticsReports.createReport.previewTopPosts')}
        </h3>
        <p className="tw-text-sm tw-text-grayscale-100">
          {t('analyticsContent.noPosts')}
        </p>
      </section>
    );
  }

  return (
    <section className="tw-space-y-3">
      {groups.map((group) => (
        <ReportContentPostTableCard
          key={group.key}
          title={t(group.titleKey)}
          columns={group.columns}
          posts={group.posts}
        />
      ))}
    </section>
  );
};

export default ReportPreviewTopPostsSection;
